import type { FlightCardData } from '@massilia/queries'

import { cn, useIsMobile } from '@massilia/ui'
import { useMemo, useState } from 'react'

import { useDebug } from '@/components/flights/debug-context'

import { FlightCardPrice } from './flight-card-price'
import { FlightInfoSection } from './flight-info-section'
import { JourneyOptionsPanel } from './journey-options-panel'

interface FlightCardProps {
  className?: string
  flight: FlightCardData
  onSelect?: (flight: FlightCardData, selectedJourneyKeys: string[]) => void
  transactionKey: string
}

export function FlightCard({ className, flight, onSelect, transactionKey }: FlightCardProps) {
  const isMobile = useIsMobile()
  const isDebug = useDebug()

  // State for expansion and journey selection
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedJourneyKeys, setSelectedJourneyKeys] = useState<string[]>(
    flight.defaultSelectedJourneyKeys
  )

  // Calculate total number of journey options
  const totalOptionsCount = useMemo(() => {
    return flight.availableJourneys.reduce((acc, segment) => acc + segment.options.length, 0)
  }, [flight.availableJourneys])

  // Check if any segment has more than one option (i.e. has alternatives to choose from)
  const hasAlternativeOptions = useMemo(() => {
    return flight.availableJourneys.some((segment) => segment.options.length > 1)
  }, [flight.availableJourneys])

  // Get the current displayed flight data based on selected journeys
  const displayedFlight = useMemo(() => {
    const updatedSegments = selectedJourneyKeys.map((key) => flight.journeySegments[key])

    // Fallback if no segments resolved at all
    if (updatedSegments.every((s) => s === undefined)) {
      return flight
    }

    // For one-way or round-trip, update outbound/return
    if (flight.segments === undefined || flight.segments.length === 0) {
      return {
        ...flight,
        outbound: updatedSegments[0] ?? flight.outbound,
        return: updatedSegments[1] ?? flight.return,
      }
    }

    // For multi-destination, update segments array
    return {
      ...flight,
      segments: updatedSegments,
    }
  }, [flight, selectedJourneyKeys])

  // Handle journey selection change
  const handleSelectionChange = (segmentIndex: number, journeyKey: string) => {
    setSelectedJourneyKeys((prev) => {
      const newKeys = [...prev]
      newKeys[segmentIndex - 1] = journeyKey
      return newKeys
    })
  }

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md',
        className
      )}
    >
      <div className="flex flex-col md:flex-row">
        {/* Left section - Flight info */}
        <div className="flex flex-1 flex-col gap-2 p-2 md:p-4">
          {/* Header with airline */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {displayedFlight.logoUrl && (
                <img
                  alt={`${displayedFlight.airline} logo`}
                  className="size-15 object-contain"
                  onError={(e) => {
                    // Hide image if it fails to load
                    e.currentTarget.style.display = 'none'
                  }}
                  src={displayedFlight.logoUrl}
                />
              )}
              <div className="text-lg font-semibold text-gray-900">{displayedFlight.airline}</div>
              {isDebug &&
                (flight.allotmentStatus?.outboundIsAllotment ||
                  flight.allotmentStatus?.returnIsAllotment) && (
                  <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                    Allotement
                    {flight.allotmentStatus.outboundIsAllotment &&
                    flight.allotmentStatus.returnIsAllotment
                      ? ' (aller + retour)'
                      : flight.allotmentStatus.outboundIsAllotment
                        ? ' (aller)'
                        : ' (retour)'}
                  </span>
                )}
            </div>
          </div>

          {/* Flight info section */}
          <FlightInfoSection
            flight={displayedFlight}
            hasAlternativeOptions={hasAlternativeOptions}
            isExpanded={isExpanded}
            onToggleExpand={() => {
              setIsExpanded((prev) => !prev)
            }}
            totalOptionsCount={totalOptionsCount}
          />
        </div>

        {/* Right section - Pricing */}
        <div className={isMobile ? 'w-full' : ''}>
          <FlightCardPrice
            flight={flight}
            onSelect={() => {
              onSelect?.(flight, selectedJourneyKeys)
            }}
            pricing={flight.pricing}
            selectedJourneyKeys={selectedJourneyKeys}
            transactionKey={transactionKey}
          />
        </div>
      </div>

      {/* Expandable journey options panel */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded ? 'max-h-100 overflow-y-auto opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <JourneyOptionsPanel
          availableJourneys={flight.availableJourneys}
          onSelectionChange={handleSelectionChange}
          selectedJourneyKeys={selectedJourneyKeys}
        />
      </div>
    </div>
  )
}
